import { useState, useCallback } from "react";

/**
 * Custom hook for managing undo/redo history
 * @param {*} initialState - Initial state value
 * @param {number} maxHistory - Maximum history size (default: 50)
 * @returns {object} - { state, setState, undo, redo, canUndo, canRedo, clearHistory }
 */
const useHistory = (initialState, maxHistory = 50) => {
  const [history, setHistory] = useState([initialState]);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Set new state and add to history
  const setState = useCallback(
    (newState) => {
      setHistory((prev) => {
        // Remove any future history if we're not at the end
        const newHistory = prev.slice(0, currentIndex + 1);

        // Add new state
        newHistory.push(newState);

        // Limit history size
        if (newHistory.length > maxHistory) {
          newHistory.shift();
          setCurrentIndex(maxHistory - 1);
        } else {
          setCurrentIndex(newHistory.length - 1);
        }

        return newHistory;
      });
    },
    [currentIndex, maxHistory]
  );

  // Undo to previous state
  const undo = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  }, [currentIndex]);

  // Redo to next state
  const redo = useCallback(() => {
    if (currentIndex < history.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  }, [currentIndex, history.length]);

  // Clear history
  const clearHistory = useCallback((newInitialState) => {
    setHistory([newInitialState]);
    setCurrentIndex(0);
  }, []);

  // Replace current state without adding to history
  const replaceState = useCallback(
    (newState) => {
      setHistory((prev) => {
        const newHistory = [...prev];
        newHistory[currentIndex] = newState;
        return newHistory;
      });
    },
    [currentIndex]
  );

  return {
    state: history[currentIndex],
    setState,
    undo,
    redo,
    canUndo: currentIndex > 0,
    canRedo: currentIndex < history.length - 1,
    clearHistory,
    replaceState,
  };
};

export default useHistory;
